import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import clientSvc from '../../../services/client/clientSvc';
import {
  IPayable,
  IPayablePagination,
  PayableResponse,
} from '../../../services/client/types';
import { aesDecryptCBC } from '../../../utils/crypt';
import {
  camelCaseMapData,
  capitalizeFirst,
  deepSnakeToCamel,
  formatCurrency,
} from '../../../utils/formater';
import { LoadState } from '../../store/types';

type PayableState = LoadState<IPayablePagination>;

export const initialState: PayableState = {
  error: '',
  loading: false,
  data: null,
};

const fetchPayables = createAsyncThunk(
  'fetchPayables',
  async (
    params:
      | { search?: string; page?: number; dateFrom?: string; dateTo?: string }
      | undefined
  ) =>
    clientSvc.getPayables({
      search: params?.search,
      page: params?.page,
      dateFrom: params?.dateFrom,
      dateTo: params?.dateTo,
    })
);

export const payablesStatus = createSlice({
  name: 'clients',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchPayables.pending, (state) => {
      state.loading = true;
      state.error = '';
    });
    builder.addCase(
      fetchPayables.fulfilled,
      (state, { payload: payablesResponse }) => {
        const payablesData: PayableResponse[] = [];
        const { results: payables, ...pagination } = payablesResponse;

        payables.forEach((payable: any) => {
          payablesData.push({
            ...payable,
            id: payable.id,
            uuid: payable.uuid,
            name: payable.client?.name ?? payable.client?.natural_person ?? '',
            payment_status: capitalizeFirst(
              payable.payment_status.toLowerCase()
            ),
            is_pending_transfer: payable.is_pending_transfer,
            is_deferred_transfer: payable.is_deferred_transfer,
            view_status: capitalizeFirst(payable.view_status.toLowerCase()),
            status: payable.status,
            date:
              payable.payment_status === 'PENDING_TO_PROCESS'
                ? payable.due_date
                : payable.created_at,
            date_long: payable.created_at_long,
            amount: `${formatCurrency(payable.amount)}`,
            des_amount: `${formatCurrency(payable.des_amount)} ${
              payable.des_currency
            }`,
            currency: payable.currency,
            payments_due_date: {
              expired: `${payable.due_date_long}`,
              status: capitalizeFirst(payable.status.toLowerCase()),
            },
            invoice_number: payable.invoice_number,
            redirect: `/payment/?origin=payable&invoice=${payable.uuid}`,
            client: {
              country: payable.client?.country ?? '',
              uuid: payable.client?.uuid ?? '',
              entityBusiness: payable.client?.entity_business ?? '',
              companyName: payable.client?.company_name ?? '',
              name: payable.client?.name ?? '',
              origin: payable.client?.origin ?? '',
              bankAccount: {
                bankName: payable.client?.bank_name ?? '',
                bankAddress: payable.client?.bank_address ?? '',
                clabe: payable.client?.clabe
                  ? aesDecryptCBC(payable.client?.clabe)
                  : '',
                routingNumber: payable.client?.routing_number
                  ? aesDecryptCBC(payable.client?.routing_number)
                  : '',
                accountNumber: payable.client?.account_number
                  ? aesDecryptCBC(payable.client?.account_number)
                  : '',
                accountType: payable.client?.account_type ?? '',
              },
            },
          } as PayableResponse);
        });
        const convertedToCamel = payablesData.map((item) =>
          camelCaseMapData<PayableResponse, IPayable>(item)
        );
        state.loading = false;
        state.data = {
          results: convertedToCamel as unknown as IPayable[],
          ...deepSnakeToCamel(pagination),
        } as IPayablePagination;
        state.error = '';
      }
    );

    builder.addCase(fetchPayables.rejected, (state) => {
      state.loading = false;
      state.data = null;
      state.error = 'ERROR';
    });
  },
});

export { fetchPayables };
export default payablesStatus.reducer;
